import { useEffect, useState } from "react";
import { Button } from "@/src/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/src/components/ui/dialog";
import { useToast } from "@/src/components/ui/use-toast";
import { fetch } from "@/src/lib/fetch";
import { cn } from "@/src/lib/utils";

/**
 * Find a Xero invoice the index never matched to this booking (raised by hand,
 * or under another contact / reference) and link it. Searches the connected
 * Xero orgs live by contact name, invoice number or reference; the booking's
 * client name is searched first.
 */
type Hit = {
  invoiceId: string; brand: "helm" | "anchor"; type: string; number: string | null; reference: string | null; status: string | null;
  contactName: string | null; date: string | null; dueDate: string | null; total: number; amountDue: number; currency: string | null;
  linkedBooking: { id: string; slug: string | null } | null; xeroUrl: string;
};
const money = (v: number, c: string | null) => new Intl.NumberFormat("en-EN", { style: "currency", currency: c || "EUR", maximumFractionDigits: 2 }).format(v);
const day = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : null);

export function XeroInvoiceSearchDialog({ bookingId, bookingLabel, brand, initialQuery, initialType, onClose, onLinked }: {
  bookingId: string; bookingLabel?: string | null; brand?: string | null; initialQuery?: string | null; initialType?: "all" | "ACCREC" | "ACCPAY"; onClose: () => void; onLinked: (hit: Hit) => void;
}) {
  const { toast } = useToast();
  const [q, setQ] = useState(initialQuery ?? "");
  const [org, setOrg] = useState<"all" | "helm" | "anchor">((String(brand ?? "").toLowerCase() as any) || "all");
  const [kind, setKind] = useState<"all" | "ACCREC" | "ACCPAY">(initialType ?? "all");
  const [hits, setHits] = useState<Hit[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [linking, setLinking] = useState<string | null>(null);

  const search = (term: string, o = org, k = kind) => {
    if (term.trim().length < 2) { setHits(null); return; }
    setSearching(true);
    fetch<{ data: Hit[] }>(`/api/finance/bookings/${bookingId}/xero/search?q=${encodeURIComponent(term.trim())}&brand=${o}&type=${k}`)
      .then((r: any) => setHits(r.data ?? []))
      .catch((e: any) => { setHits([]); toast({ title: "Xero search failed", description: e?.message, variant: "destructive" }); })
      .finally(() => setSearching(false));
  };
  useEffect(() => { if (initialQuery) search(initialQuery, org, initialType ?? "all"); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const link = (h: Hit, force = false) => {
    setLinking(h.invoiceId);
    fetch(`/api/finance/bookings/${bookingId}/xero/link`, "POST", { invoiceId: h.invoiceId, brand: h.brand, force })
      .then(() => { toast({ title: "Invoice linked", description: `${h.number ?? h.invoiceId.slice(0, 8)} (${h.brand.toUpperCase()}) is now on this booking.` }); onLinked(h); })
      .catch((e: any) => toast({ title: "Couldn't link", description: e?.message, variant: "destructive" }))
      .finally(() => setLinking(null));
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>Find {initialType === "ACCPAY" ? "supplier bill" : "invoice"} in Xero{bookingLabel ? ` · ${bookingLabel}` : ""}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600">Search the Xero org by the client's or supplier's name, the document number or its reference — client invoices and supplier bills. Linking puts the document on this booking so payments can be allocated against it.</p>
        <form className="flex flex-wrap gap-2" onSubmit={(e) => { e.preventDefault(); search(q); }}>
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="client name, invoice number, or reference" className="min-w-0 flex-1 rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-400" />
          <select value={org} onChange={(e) => { const o = e.target.value as any; setOrg(o); if (q.trim().length >= 2) search(q, o); }} className="rounded-md border border-slate-200 bg-white px-2 py-2 text-sm">
            <option value="all">Both orgs</option>
            <option value="helm">HELM</option>
            <option value="anchor">Anchor</option>
          </select>
          <select value={kind} onChange={(e) => { const k = e.target.value as any; setKind(k); if (q.trim().length >= 2) search(q, org, k); }} className="rounded-md border border-slate-200 bg-white px-2 py-2 text-sm">
            <option value="all">Invoices & bills</option>
            <option value="ACCREC">Client invoices</option>
            <option value="ACCPAY">Supplier bills</option>
          </select>
          <Button type="submit" variant="outline" disabled={searching}>{searching ? "…" : "Search"}</Button>
        </form>
        <div className="max-h-96 space-y-2 overflow-y-auto">
          {hits && hits.length === 0 && <p className="text-sm text-slate-500">No invoice found for "{q}" — try the surname only, or the invoice number from Xero.</p>}
          {(hits ?? []).map((h) => (
            <div key={h.invoiceId} className="flex items-start justify-between gap-3 rounded-lg border px-3 py-2 text-sm">
              <div className="min-w-0">
                <p className="truncate">
                  <span className={cn("mr-1.5 rounded-full px-1.5 py-0.5 text-[10px] font-medium uppercase", h.type === "ACCPAY" ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800")}>{h.type === "ACCPAY" ? "Bill" : "Invoice"}</span>
                  <span className="font-medium">{h.number ?? h.invoiceId.slice(0, 8)}</span>
                  <span className="ml-1.5 rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium uppercase text-slate-600">{h.brand}</span>
                  {h.status && <span className="ml-1.5 rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-600">{h.status}</span>}
                  {h.contactName && <span className="ml-1.5 text-slate-600">· {h.contactName}</span>}
                </p>
                <p className="text-xs text-slate-500">
                  {[`${money(h.total, h.currency)} total`, h.amountDue > 0 ? `${money(h.amountDue, h.currency)} due` : "paid", h.reference, day(h.date) ? `issued ${day(h.date)}` : null, day(h.dueDate) ? `due ${day(h.dueDate)}` : null].filter(Boolean).join(" · ")}
                </p>
                {h.linkedBooking && <p className="text-xs text-amber-700">{h.linkedBooking.id === bookingId ? "Already on this booking" : `Already linked to booking ${h.linkedBooking.slug ?? h.linkedBooking.id.slice(0, 8)}`}</p>}
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <a className="text-xs text-blue-600 hover:underline" href={h.xeroUrl} target="_blank" rel="noreferrer noopener">Open ↗</a>
                <Button size="sm" variant={h.linkedBooking ? "outline" : "default"} disabled={linking === h.invoiceId || h.linkedBooking?.id === bookingId}
                  onClick={() => (h.linkedBooking && h.linkedBooking.id !== bookingId ? window.confirm("This invoice is already on another booking. Move it here?") && link(h, true) : link(h))}>
                  {linking === h.invoiceId ? "Linking…" : "Link"}
                </Button>
              </div>
            </div>
          ))}
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
